<script setup lang="ts">
import * as Sentry from '@sentry/nuxt'

import InfluencerProfile from '~/components/influencer/Profile.vue'
import InfluencerProfileEditConfirm from '~/components/influencer/profile/edit/confirm.vue'
import ConfirmationInfluencerSignup from '~/components/nav/confirmations/influencerSignup.vue'
import ConfirmationInfluencerValidation from '~/components/nav/confirmations/influencerValidation.vue'
import CampaignFooter from '~/components/ui/CampaignFooter/index.vue'

import { useCartSnackbars } from '~/composables/useCartSnackbars'
import { useProgressivePromosCheck } from '~/composables/useProgressivePromosCheck'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'
import { useSegmentTrackCartAdditions } from '~/composables/useSegmentTrackCartAdditions'

import { useDraftStore } from '~/stores/draft'
import { useInfluencersStore } from '~/stores/influencers'
import { useInfluencersRecommendationsStore } from '~/stores/influencersRecommendations'
import { useTagStore } from '~/stores/tag'
import { useUserStore } from '~/stores/user'
import { useUserBandStore } from '~/stores/userBand'
import { useUserFavoritesStore } from '~/stores/userFavorites'
import { useUserInfluencerStore } from '~/stores/userInfluencer'

import pictureBuildHelper from '~/helpers/images/picture_build'

import { isString } from '~/utils/type-guards'

import type { Influencer } from '~/types/influencer'
import type InfluencerInteraction from '~/types/influencerInteraction'
import type { Locale } from '~/types/locale'

export interface _AsyncDataSharedSlug {
  influencer: Influencer
  influencerPicture: string
  suggested: Influencer['id'][]
}

definePageMeta({
  middleware: async (to) => {
    const { $pinia } = useNuxtApp()
    const userStore = useUserStore($pinia)
    const draftStore = useDraftStore($pinia)

    const {
      params: { slug },
      query: { draftId: draftIdParam },
    } = to

    // avoids infinity loops
    if (!userStore.IS_BAND || !draftIdParam) return

    const draftId = Number(draftIdParam)

    if (draftStore.id !== draftId) {
      const draft = await draftStore.FETCH_BY_ID(draftId).catch((err) => {
        Sentry.captureException(err)
      })

      if (!draft) return

      draftStore.SET(draft)
      // get the first batch of draft infs, but not all of them so that pages can load
      await draftStore.GET_CURRENT_DRAFT_INFLUENCERS()
      // continue to get the rest of the influencers if there are any without blocking the page
      draftStore.GET_CURRENT_DRAFT_INFLUENCERS({ fetchAll: true })
    }

    if (draftId) await draftStore.FETCH_MISSING_INFLUENCERS()
  },
})

const { params, query } = useRoute()
const { t, locale: LANG } = useI18n()
const {
  public: { ENV_NAME },
} = useRuntimeConfig()
const { coreFetch } = useProvideCoreFetch()

const { FETCH: FETCH_TAGS, GET_TAGS_FROM_IDS, GET_TAG_FROM_ID } = useTagStore()
const { FETCH_SLUG, FETCH_SET } = useInfluencersStore()
const { authorized: curatorIsAuthorized } = storeToRefs(
  useUserInfluencerStore(),
)
const { FETCH_BUCKETS, FETCH_INFLUENCERS } = useUserFavoritesStore()
const { id: BAND_ID } = storeToRefs(useUserBandStore())
const { id: DRAFT_ID } = storeToRefs(useDraftStore())
const { IS_INF } = storeToRefs(useUserStore())
const { FETCH_RECOMMENDATION } = useInfluencersRecommendationsStore()

useProgressivePromosCheck()

const isRouterGoingToPreviousPage = ref<boolean>(false)

useSegmentTrackCartAdditions()
useCartSnackbars()

onBeforeRouteLeave((_to, _from, next) => {
  isRouterGoingToPreviousPage.value = true
  next()
})

const [{ data: influencerRef }] = await Promise.all([
  useAsyncData('influencerRef', () => FETCH_SLUG(params.slug as string)),
  callOnce('FETCH_TAGS', FETCH_TAGS),
]).catch(() => {
  /**
   * This is required for proper indexing and SEO value
   * If we start
   * re-indexing our main content to the same page
   * we'll probably get red-flagged.
   * So I'm skipping the redirection to / on catch here.
   */
  throw createError({
    message: 'Influencer not found',
    statusCode: 404,
  })
})

const influencer = influencerRef.value
if (!influencer) {
  throw createError({
    message: 'Influencer not found',
    statusCode: 404,
  })
}

await useLazyAsyncData(async () => {
  await FETCH_SET([influencer.id])
  return FETCH_RECOMMENDATION()
})

const { data: interactions } = await useLazyAsyncData<InfluencerInteraction[]>(
  () => {
    if (IS_INF.value) return Promise.resolve([])

    return coreFetch.$get<InfluencerInteraction[]>(
      `/band/history/?influencer_id=${influencer.id}`,
    )
  },
  {
    default: () => [],
  },
)

const { data: suggested } = await useLazyAsyncData<Influencer['id'][]>(
  async () => {
    const ids = await coreFetch.$get(
      `recommendation/influencer_similarity/profile/${influencer.id}/`,
      {
        query: {
          band_id: BAND_ID.value || undefined,
          draft_id: DRAFT_ID.value || undefined,
        },
      },
    )
    await FETCH_SET(ids)
    return ids
  },
  { default: () => [] },
)

await useLazyAsyncData(async () => {
  await FETCH_BUCKETS()
  await FETCH_INFLUENCERS({ fetchRecommended: false })
})

// Setup head data
const {
  entity: influencerName,
  tags: {
    identity: { influencer_kind: influencerKindTagIds },
  },
} = influencer
const influencerKindTags = GET_TAGS_FROM_IDS(influencerKindTagIds)

const translatedInfluencerKinds = influencerKindTags.map(
  ({ name }) => t(`tags.influencer_kind.${name}`) as string,
)
// We want a maximum of 50 characters in order for titles to display correctly in Google
const maximumTitleLength = 50
let remainingSpace =
  maximumTitleLength -
  (influencerName.length + (t('shared.onGroover') as string).length)
const trimmedKinds = translatedInfluencerKinds.reduce(
  (acc, kind, index, array) => {
    remainingSpace -=
      kind.length + (index === array.length - 1 ? ' '.length : ', '.length)
    if (remainingSpace >= 0) acc.push(kind.toLowerCase())

    return acc
  },
  [] as string[],
)

const influencerCountry: string | undefined = GET_TAG_FROM_ID(
  influencer.tags?.identity.country[0] ?? 0,
)?.name

const title = t('metaTags.influencerProfileSlug.title', {
  influencerName,
  space: trimmedKinds.length ? ' ' : '',
  kinds: trimmedKinds.join(', '),
})

const description = t('metaTags.influencerProfileSlug.description', {
  influencerName,
})

const influencerPicture = pictureBuildHelper({
  kind: 'influencer',
  slug: influencer.has_profile_picture ? influencer.slug : undefined,
  size: '200_200',
  target: 'profile_picture',
})

/**
 * This is to avoid duplicate indexing on google
 */
const slugIsNotId = isString(params.slug) && params.slug.match(/\D/gi)

useHead({
  title,
  meta: [
    {
      name: 'description',
      key: 'description',
      content: description,
    },
    {
      key: 'og:title',
      property: 'og:title',
      content: title,
    },
    {
      key: 'og:description',
      property: 'og:description',
      content: description,
    },
    {
      property: 'og:image',
      key: 'og:image',
      content: influencerPicture,
    },
    {
      key: 'robots',
      name: 'robots',
      content:
        slugIsNotId &&
        ENV_NAME === 'prod' &&
        influencer.authorized &&
        !influencer.is_scout &&
        !influencer.disable_indexing
          ? 'index, follow'
          : 'noindex, follow',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Corporation',
        name: influencerName,
        description: influencer[`description_${LANG.value as Locale}` as const],
        url: `https://www.groover.co/influencer/profile/${influencer.slug}/`,
        address: influencerCountry
          ? {
              '@type': 'PostalAddress',
              addressCountry: t(`tags.country.${influencerCountry}`),
            }
          : undefined,
        logo: influencerPicture,
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ProfilePage',
        name: influencerName,
      }),
    },
  ],
})

const computedDraftId = computed<number>(() => Number(query.draftId ?? 0))
</script>

<template>
  <div
    id="ignoreFontUpscale"
    class="influencerProfileSlugWrapper ignoreFontUpscale tw-relative tw-w-full"
  >
    <InfluencerProfileEditConfirm />

    <div v-if="!curatorIsAuthorized" class="notAuthorized">
      <ConfirmationInfluencerValidation />
      <ConfirmationInfluencerSignup />
    </div>
    <InfluencerProfile
      v-if="!isRouterGoingToPreviousPage"
      :influencer="influencer"
      :interactions="interactions"
      :suggested="suggested"
    />
    <CampaignFooter
      v-if="!IS_INF"
      page="profile"
      :influencer="influencer"
      :draft-id="computedDraftId"
    />
  </div>
</template>
